import dayjs from "dayjs";
import { Position, PositionImage, StatusConst, UserData } from "../interfaces";
import {
  BLEED_WIDTH,
  BROWSER_MAX_AREA,
  BROWSER_MAX_H_W,
  MAX_HEIGHT,
  MAX_WIDTH,
  MM_TO_INCH,
} from "./constants";
import { Storage } from "aws-amplify";
import { UploadFileProps } from "../components/AccountBulkUpload";

export async function asyncForEach(array: any, callback: any) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

export const formatDateAndTime = (
  dateString: string,
  format: string = "DD.MM.YYYY"
) => {
  if (!dateString) return "";

  return dayjs(dateString).format(format);
};

/**
 * This method is used calculate timezone difference and add or subtract it from date and return date in UTC.
 * @param date - Date
 * @param isUpload - to check timezone will be deleted or added
 * @returns - UTC date
 */
export const handleTimeZone = (date: Date, isUpload: boolean) => {
  const timeZoneDifference = date.getTimezoneOffset();
  const utcDate = isUpload
    ? dayjs(date).subtract(timeZoneDifference, "minute").format()
    : dayjs(date).add(timeZoneDifference, "minute").format();
  return utcDate;
};

export const scaleImage = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => {
  const aspectRatio = width / height;

  if (aspectRatio < 1) {
    const scaledHeight = Math.min(MAX_HEIGHT, height * 0.8, height);
    const scaledWidth = scaledHeight / (height / width);
    return { scaledWidth, scaledHeight };
  } else {
    const scaledWidth = Math.min(MAX_WIDTH, width * 0.8, width);
    const scaledHeight = scaledWidth / aspectRatio;
    return { scaledWidth, scaledHeight };
  }
};

export const resetInset = (index: number) => {
  const imgPreview = document.getElementById(
    `image-preview_${index}`
  ) as HTMLImageElement;

  if (imgPreview) {
    imgPreview.style.outlineWidth = "0px";
    imgPreview.style.outlineOffset = "0px";
  }

  const pdfPreview = document.getElementById(
    "image-preview_pdf"
  ) as HTMLImageElement;

  if (pdfPreview) {
    pdfPreview.style.outlineWidth = "0px";
    pdfPreview.style.outlineOffset = "0px";
  }
};

export const getOutline = ({
  image,
  item,
}: {
  image: HTMLImageElement;
  item: Position;
}): string => {
  const width = image.naturalWidth;
  const height = image.naturalHeight;

  if (item.productGroup === "GO") {
    const _width = item.widthByMm + BLEED_WIDTH * 2;
    const _height = item.heightByMm + BLEED_WIDTH * 2;

    const wPixel = (width / _width) * BLEED_WIDTH;
    const pixelHeight = (width / _width) * _height;
    const hPixel = (pixelHeight / _height) * BLEED_WIDTH;

    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    canvas.width = width;
    canvas.height = height;
    const context = canvas?.getContext("2d") as CanvasRenderingContext2D;
    drawLines({ canvas, context, wPixel, hPixel });

    return canvas.toDataURL();
  } else if (item.productGroup === "EXPO") {
    const wBLEED_WIDTH = Math.max((item.widthByMm * 1) / 100, BLEED_WIDTH); // 1% or 20mm
    const hBLEED_WIDTH = Math.max((item.heightByMm * 1) / 100, BLEED_WIDTH); // 1% or 20mm

    const _width = item.widthByMm + wBLEED_WIDTH * 2;
    const _height = item.heightByMm + hBLEED_WIDTH * 2;

    const wPixel = (width / _width) * wBLEED_WIDTH * 2;
    const hPixel = (height / _height) * hBLEED_WIDTH * 2;

    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    canvas.width = width;
    canvas.height = height;
    const context = canvas?.getContext("2d") as CanvasRenderingContext2D;
    drawLines({ canvas, context, wPixel, hPixel });

    return canvas.toDataURL();
  } else {
    return "";
  }
};

export const drawLines = ({
  canvas,
  context,
  wPixel,
  hPixel,
}: {
  canvas: HTMLCanvasElement;
  context?: CanvasRenderingContext2D;
  wPixel: number;
  hPixel: number;
}) => {
  if (context) {
    // left
    context.strokeStyle = "#ffffff69";
    context.beginPath();
    context.moveTo(0, 0);
    context.lineWidth = wPixel;
    context.lineTo(0, canvas.height);
    context.stroke();
    context.closePath();

    // bottom
    context.beginPath();
    context.moveTo(0, canvas.height);
    context.lineWidth = hPixel;
    context.lineTo(canvas.width, canvas.height);
    context.stroke();
    context.closePath();

    // right
    context.beginPath();
    context.moveTo(canvas.width, canvas.height);
    context.lineWidth = wPixel;
    context.lineTo(canvas.width, 0);
    context.stroke();
    context.closePath();

    // top
    context.beginPath();
    context.moveTo(0, 0);
    context.lineWidth = hPixel;
    context.lineTo(canvas.width, 0);
    context.stroke();
    context.closePath();

    // marker
    context.beginPath();
    context.moveTo(0, 0);
    context.strokeStyle = "#fa00ff";
    context.lineWidth = 1.4;
    context.strokeRect(
      wPixel / 2,
      hPixel / 2,
      canvas.width - wPixel,
      canvas.height - hPixel
    );
    context.closePath();
  }
};

/**
 * This method is used to tell that created canvas is supported or not by data url
 * @param { string } dataUrl - canvas data url
 * @returns { boolean }
 */
export const isExceedCanvasSize = (dataUrl: string): boolean => {
  if (dataUrl === "data:,") {
    return true;
  }
  return false;
};

/**
 * This method is used to calculate image size support for canvas by imageHtml.
 * @param { HTMLImageElement } image - Html image to get height and width of image
 * @returns { boolean }
 */
export const isExceedCanvasSizeByImage = (image: HTMLImageElement): boolean => {
  const imageArea = image.width * image.height;
  if (
    image.width > BROWSER_MAX_H_W ||
    image.height > BROWSER_MAX_H_W ||
    imageArea > BROWSER_MAX_AREA
  ) {
    return true;
  }
  return false;
};

export const getRemainingCount = (item: Position) => {
  return item.White
    ? ""
    : item.quantity -
        (item.positionImage?.reduce(
          (acc: number, curr: PositionImage) => acc + curr.quantity,
          0
        ) || 0);
};

export const getRemainingColor = (item: Position) => {
  if (item.White) return "green";

  const remaining =
    item.quantity -
    (item.positionImage?.reduce(
      (acc: number, curr: PositionImage) => acc + curr.quantity,
      0
    ) || 0);

  if (remaining === 0) return "green";
  else return "red";
};

export const downloadImage = async (signedUrl: string) => {
  return new Promise(function (resolve, reject) {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", signedUrl);
      xhr.responseType = "blob";
      xhr.onerror = function () {
        reject("Network error.");
      };
      xhr.onload = function () {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject("Loading error:" + xhr.statusText);
        }
      };
      xhr.send();
    } catch (err: any) {
      reject(err.message);
    }
  });
};

export const createThumbnail = async (
  img: HTMLImageElement,
  name: string,
  type: string
): Promise<File> => {
  const image = img.cloneNode() as HTMLImageElement;
  const width = image.width;
  const height = image.height;
  const aspectRatio = width / height;
  const scaledWidth = 500;
  const scaledHeight = scaledWidth / aspectRatio;

  image.style.width = `${scaledWidth}px`;
  image.style.height = `${scaledWidth}px`;

  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  canvas.width = scaledWidth;
  canvas.height = scaledHeight;

  const context = canvas?.getContext("2d");
  context?.drawImage(image, 0, 0, scaledWidth, scaledHeight);

  return await new Promise((resolve) =>
    canvas.toBlob((blob) => blob && resolve(new File([blob], name, { type })))
  );
};

export const createImgThumbnail = async (
  index: number,
  name: string,
  type: string,
  imgElement?: HTMLImageElement
) => {
  let image = imgElement;
  if (!imgElement) {
    image = document.getElementById(
      `image-preview_${index}`
    ) as HTMLImageElement;
  }
  if (image) return await createThumbnail(image, name, type);

  return null;
};

export const createPdfThumbnail = async (name: string, type: string) => {
  const image = document.getElementById(
    "image-preview_pdf"
  ) as HTMLImageElement;

  if (image) return await createThumbnail(image, name, type);

  return null;
};

/**
 * This method is second version of calculating ratio and dpi of image corresponding to positions.
 * @param width width of uploaded file
 * @param height height of uploaded file
 * @returns Object which will have ratio of image is fitting the position or not and dpi of that image
 */
export const calculateImageV2 = ({
  width,
  height,
  item,
}: {
  width: number;
  height: number;
  item: Position;
}) => {
  const { x: _x, y: _y } = getRatio(item.widthByMm, item.heightByMm); // product ratio
  const { x, y } = getRatio(width, height); // pixel ratio
  const positionAspectRatio = Math.max(_x, _y);
  const fileAspectRatio = Math.max(x, y);
  const deviation = 100 - (100 / positionAspectRatio) * fileAspectRatio;
  const fitWithin = -5 <= deviation && 5 >= deviation;
  const fit = (x === _x && y === _y) || (x === _y && y === _x);
  const _ratio = fit
    ? { x, y, fit }
    : fitWithin
    ? { x: round(x, 2), y: round(y, 2), fit: fitWithin }
    : { x: round(x, 2), y: round(y, 2), fit: false };

  const productWidthInInches = item.widthByMm * MM_TO_INCH;
  const productHeightInInches = item.heightByMm * MM_TO_INCH;

  const pixelAdditionHeightWidth = width + height;
  const productAdditionHeightWidth =
    productWidthInInches + productHeightInInches;

  const _dpi = round(pixelAdditionHeightWidth / productAdditionHeightWidth, 2);

  return { _ratio, _dpi };
};

export const calculateImage = ({
  width,
  height,
  item,
}: {
  width: number;
  height: number;
  item: Position;
}) => {
  const { x: _x, y: _y } = getRatio(item.widthByMm, item.heightByMm); // product ratio
  const { x, y } = getRatio(width, height); // pixel ratio
  const fitWithin =
    (Math.abs(_x - x) <= _x * 0.05 && Math.abs(_y - y) <= _x * 0.05) ||
    (Math.abs(_x - y) <= _x * 0.05 && Math.abs(_y - x) <= _x * 0.05);

  const fit = (x === _x && y === _y) || (x === _y && y === _x);

  const _ratio = fit
    ? { x, y, fit }
    : fitWithin
    ? { x: round(x, 0), y: round(y, 0), fit: fitWithin }
    : { x: round(x, 2), y: round(y, 2), fit: false };

  const productWidthInInches = item.widthByMm * MM_TO_INCH;
  const productHeightInInches = item.heightByMm * MM_TO_INCH;

  const pixelAdditionHeightWidth = width + height;
  const productAdditionHeightWidth =
    productWidthInInches + productHeightInInches;

  const _dpi = round(pixelAdditionHeightWidth / productAdditionHeightWidth, 2);

  return { _ratio, _dpi };
};

export const updateProgress = ({
  line,
  progress,
  e,
}: {
  line?: HTMLElement;
  progress?: HTMLElement;
  e: any;
}) => {
  const loaded = e.loaded;
  const total = e.total;
  const percentageLoaded = (loaded / total) * 100;
  if (progress) progress.innerText = Math.floor(percentageLoaded) + "%";
  if (line) line.style.width = Math.floor(percentageLoaded) + "%";
};

export const loadProgress = async (
  element: HTMLElement,
  timeout: number,
  callback: () => {}
) => {
  if (element) {
    let percent: number = 0;
    let step: number = 1;
    const interval = setInterval(() => {
      if (percent >= 96) {
        clearInterval(interval);
        percent = 100;
        callback();
      } else {
        percent += step;
        if (percent % 5 === 0) step += 1;
      }
      element.innerText = Math.floor(percent) + "%";
    }, timeout / 20); // 20 frame
  }
};

/**
 * This method is used to get progress of preview image
 * @param item - uploaded item
 * @param setPdfPreviewProgress - progress of image preview load
 * @param callbackCompleted - callback function when completed progress
 */
export const loadProgressBulkUpload = async (
  item: UploadFileProps,
  setPdfPreviewProgress: React.Dispatch<React.SetStateAction<number>>,
  callbackCompleted: () => void
) => {
  const callback = async () => {
    const timerId = setInterval(async () => {
      const fileName = item.positionImage?.thumbnail.split(".")[0];
      const signedUrl = await Storage.get(`${fileName}/photo/3.jpg`);
      const res = await fetch(signedUrl);
      if (res.status === 200) {
        callbackCompleted();
        clearInterval(timerId);
      }
    }, 300);
  };
  const download = ((item.file.size / 1000 / 1000) * 60) / 100; // 3 seconds for 200mb file
  const duration = (4 + download) * 1000;
  let percent: number = 0;
  let step: number = 1;
  const interval = setInterval(() => {
    if (percent >= 96) {
      clearInterval(interval);
      percent = 100;
      callback();
    } else {
      percent += step;
      if (percent % 5 === 0) step += 1;
    }
    setPdfPreviewProgress(Math.floor(percent));
  }, duration / 20); // 20 frame
};

export const gcd = (a: number, b: number) => {
  let temp: number, m: number;
  if (b > a) {
    temp = a;
    a = b;
    b = temp;
  }
  while (b !== 0) {
    m = a % b;
    a = b;
    b = m;
  }
  return a;
};

export const getRatio = (x: number, y: number): { x: number; y: number } => {
  if (x < y) {
    return { x: 1, y: y / x };
  } else if (x > y) {
    return { x: x / y, y: 1 };
  } else {
    return { x: 1, y: 1 };
  }
  // const c = gcd(x, y);
  // return { x: x / c, y: y / c };
};

//

export const hasDashboardPermission = (user?: UserData) => {
  return user?.isAdmin || user?.isManufacturer;
};

export const round = (num: number, decimal: number) => {
  num = Math.round((num + "e" + decimal) as any);
  return Number(num + "e" + -decimal);
};

export const obfuscateEmail = (email = ""): string => {
  const getMaskedString = (str: string) => {
    if (str.length < 2) return "*";
    if (str.length === 2) return "*" + str[1];
    return str.substring(0, 2) + "*".repeat(str.length - 2);
  };

  return email
    .split("@")
    .map((s) => getMaskedString(s))
    .join("@");
};

export const getFileSizeInMb = (size: number) => {
  return (size / 1024 / 1024).toFixed(2) ?? size;
};

export const getFileNameWithoutFolderName = (key: string) => {
  return key.split("/").slice(-1)[0] ?? key;
};

export const getExtensionFileName = (fileName: string) => {
  return fileName.split(".").pop() ?? "jpg";
};

export const getCleanFileName = (fileName: string) => {
  // Normalize the string to decompose accented characters.
  // Then use regex to remove the accents, and finally replace other special characters.
  return fileName
    .normalize("NFD") // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/[^a-zA-Z0-9.-]/g, "_") // Replace characters that are not alphanumeric, dash, or period with underscore
    .replace(/_+/g, "_"); // Replace multiple underscores with a single underscore
};

// check status for customer side editable access
export const isEditableCustomerView = (status: StatusConst) => {
  const statusToBeDisabled = [
    StatusConst.Uploaded,
    StatusConst.ReUploaded,
    StatusConst.Production,
    StatusConst.ProductionFinished,
  ];
  if (statusToBeDisabled.includes(status)) {
    return false;
  }
  return true;
};
